import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { useScenarioListener } from '../../sockets/scenarios.util';
import { ScenarioSafe } from '../../types/scenario-modeling.types';
import { useAppQuery } from '../use-app-query.hook';

export const useScenarioSafes = ({
  scenarioId,
  numberOfFetchedItems = 100,
  currentPage = 1,
}: {
  currentPage?: number;
  numberOfFetchedItems?: number;
  scenarioId: string;
}) => {
  const queryClient = useQueryClient();
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * numberOfFetchedItems;
  const { data, isLoading, refetch } = useAppQuery<
    { data: ScenarioSafe[]; totalPages: number },
    HttpException
  >({
    queryKey: [
      QueryKey.GET_SCENARIO_SAFES,
      {
        page: currentPage,
        skip,
        numberOfFetchedItems,
      },
    ],
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.SCENARIOS, routePath: [scenarioId, 'safe'] },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip,
          },
        },
      ),
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SCENARIO_SAFES] });
  }, [queryClient]);

  useScenarioListener('safes-updated', () =>
    queryClient.refetchQueries({
      queryKey: [QueryKey.GET_SCENARIO_SAFES],
    }),
  );

  return {
    invalidateQuery,
    scenarioSafes: data?.data,
    totalPages: data?.totalPages,
    isLoading,
    refetch,
  };
};
